// ---------------------------------------------------------------------------
// MONEY PAGES — high-intent commercial landing pages.
//
//   /en/leads/roofing-leads-california          (statewide, California)
//   /en/leads/roofing-leads-texas               (statewide, every other state + DC)
//   /en/leads/roofing-leads-riverside-ca        (California metro)
//   /en/leads/concrete-leads-riverside-county   (California county)
//
// Generated from SEO_TRADES × (statewide in all 50 states + DC, top California
// metros, top California counties). Single source of truth for routing,
// internal links (state pages, Leads hub, trade pages) and the sitemap edge
// function, which mirrors the slug rule in `stateSlugName` below.
// ---------------------------------------------------------------------------

import { MONEY_TRADES, SEO_TRADES, SeoTrade } from './trades';
import { COUNTIES, CITIES, TOP_METRO_SLUGS, TOP_COUNTY_SLUGS } from './locations';
import { STATES, UsState, stateBySlug, hasCityPages, citySlug } from './states';

export interface MoneyPage {
  /** URL slug under /leads/ */
  slug: string;
  trade: SeoTrade;
  /** 'state' | 'city' | 'county' */
  scope: 'state' | 'city' | 'county';
  /** Human place label: "California", "Texas", "Riverside, CA", "Riverside County" */
  place: string;
  /** Spanish place label (statewide pages, e.g. "Nueva York"). Falls back to `place`. */
  placeEs?: string;
  /** Upper-case postal code the live project feed is filtered to ("TX"). */
  stateCode: string;
  /** Link back into the location hierarchy. */
  citySlug?: string;
  countySlug?: string;
}

/** "New York" -> "new-york"; DC -> "district-of-columbia" (the sitemap edge
 *  function uses the same rule — keep both in sync). */
export const stateSlugName = (st: UsState) =>
  st.code === 'DC'
    ? 'district-of-columbia'
    : st.en.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');


const build = (): MoneyPage[] => {
  const out: MoneyPage[] = [];

  // 1. California statewide pages for every money trade (original inventory).
  MONEY_TRADES.forEach((trade) => {
    out.push({ slug: `${trade.slug}-leads-california`, trade, scope: 'state', place: 'California', stateCode: 'CA' });
  });

  // 2. Trade × top California metro.
  const metros = TOP_METRO_SLUGS.map((slug) => CITIES.find((c) => c.slug === slug)).filter(Boolean) as typeof CITIES;
  MONEY_TRADES.forEach((trade) => {
    metros.forEach((city) => {
      out.push({
        slug: `${trade.slug}-leads-${city.slug}-ca`,
        trade,
        scope: 'city',
        place: `${city.name}, CA`,
        stateCode: 'CA',
        citySlug: city.slug,
        countySlug: city.county,
      });
    });
  });

  // 3. Trade × top California county.
  const counties = TOP_COUNTY_SLUGS.map((slug) => COUNTIES.find((x) => x.slug === slug)).filter(Boolean) as typeof COUNTIES;
  MONEY_TRADES.forEach((trade) => {
    counties.forEach((county) => {
      out.push({
        slug: `${trade.slug}-leads-${county.slug}`,
        trade,
        scope: 'county',
        place: `${county.name} County`,
        stateCode: 'CA',
        countySlug: county.slug,
      });
    });
  });

  // 4. Buyer-type California statewide pages (subcontractor / GC / commercial intent).
  ['general-contracting', 'commercial-construction', 'residential-construction'].forEach((slug) => {
    const trade = SEO_TRADES.find((t) => t.slug === slug);
    if (trade && !out.some((m) => m.slug === `${slug}-leads-california`)) {
      out.push({ slug: `${slug}-leads-california`, trade, scope: 'state', place: 'California', stateCode: 'CA' });
    }
  });

  // 5. NATIONWIDE statewide pages — every money trade in every other state + DC,
  //    modeled on the California statewide pages. Appended last so the order
  //    (and therefore every existing internal-link slice) is unchanged.
  STATES.filter((st) => st.code !== 'CA').forEach((st) => {
    MONEY_TRADES.forEach((trade) => {
      const slug = `${trade.slug}-leads-${stateSlugName(st)}`;
      if (out.some((m) => m.slug === slug)) return;
      out.push({ slug, trade, scope: 'state', place: st.en, placeEs: st.es, stateCode: st.code });
    });
  });

  return out;
};

export const MONEY_PAGES: MoneyPage[] = build();

const BY_SLUG = new Map(MONEY_PAGES.map((m) => [m.slug, m]));
export const moneyPageBySlug = (slug: string) => BY_SLUG.get(slug);

/** Localized place label. */
export const moneyPlace = (m: MoneyPage, lang: 'en' | 'es') => (lang === 'es' ? m.placeEs || m.place : m.place);

/** Every statewide money page for one state (state landing page + Leads hub). */
export const statewideMoneyPages = (stateCode: string) =>
  MONEY_PAGES.filter((m) => m.scope === 'state' && m.stateCode === stateCode.toUpperCase());

/**
 * Related money pages for internal linking (same trade, different place).
 * Statewide pages prefer other STATES (region neighbours first) so a Texas page
 * links to Oklahoma/Louisiana rather than to ten California metros.
 */
export const relatedMoneyPages = (page: MoneyPage, max = 10) => {
  const same = MONEY_PAGES.filter((m) => m.trade.slug === page.trade.slug && m.slug !== page.slug);
  if (page.scope !== 'state') return same.slice(0, max);
  const region = STATES.find((s) => s.code === page.stateCode)?.region;
  const regionOf = (code: string) => STATES.find((s) => s.code === code)?.region;
  const states = same.filter((m) => m.scope === 'state');
  const near = states.filter((m) => regionOf(m.stateCode) === region);
  const far = states.filter((m) => regionOf(m.stateCode) !== region);
  return [...near, ...far].slice(0, max);
};

/** Other trades in the same place (cross-sell links, no orphan pages). */
export const siblingMoneyPages = (page: MoneyPage, max = 10) =>
  MONEY_PAGES.filter(
    (m) => m.slug !== page.slug && m.place === page.place && m.trade.slug !== page.trade.slug
  ).slice(0, max);

// ---------------------------------------------------------------------------
// CITY MONEY PAGES OUTSIDE CALIFORNIA — /leads/{trade}-leads-{city}-{xx}
//
//   /en/leads/flooring-leads-orlando-fl
//
// Modeled on the California metro pages (`…-leads-riverside-ca`), but the
// city set is NOT a static catalog: it is the top CITY_MONEY_PAGES_PER_STATE
// cities of each non-California state from the browse-listings `cityIndex`
// aggregate. So these slugs are PARSED (after the static catalog lookup
// misses) rather than enumerated. A parsed page whose city is not currently
// in the state's top list still renders (old links never 404) but is noindex.
// The sitemap edge function emits exactly the indexable set with this rule.
// ---------------------------------------------------------------------------
export interface CityMoneyRoute {
  trade: SeoTrade;
  st: UsState;
  /** Normalised city slug from the URL ("orlando", "grand-prairie"). */
  citySlug: string;
}

/** "Flooring" + "Grand Prairie" + TX -> "flooring-leads-grand-prairie-tx". */
export const cityMoneySlug = (tradeSlug: string, city: string, st: UsState) =>
  `${tradeSlug}-leads-${citySlug(city)}-${st.slug}`;

// Longest trade slug first so "commercial-construction" never loses to a shorter prefix.
const TRADES_LONGEST_FIRST = [...MONEY_TRADES].sort((a, b) => b.slug.length - a.slug.length);

export const parseCityMoneySlug = (raw: string): CityMoneyRoute | null => {
  const s = (raw || '').trim().toLowerCase();
  if (!/^[a-z0-9-]{1,160}$/.test(s) || BY_SLUG.has(s)) return null;
  const trade = TRADES_LONGEST_FIRST.find((t) => s.startsWith(`${t.slug}-leads-`));
  if (!trade) return null;
  const m = s.slice(trade.slug.length + '-leads-'.length).match(/^([a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?)-([a-z]{2})$/);
  if (!m) return null;
  const st = stateBySlug(m[2]);
  // California metros are a static catalog (…-ca) — never parsed.
  if (!st || !hasCityPages(st)) return null;
  return { trade, st, citySlug: m[1] };
};

/** Every money-trade lead page for one non-California city (cross-links). */
export const cityMoneyPages = (st: UsState, city: string) =>
  MONEY_TRADES.map((trade) => ({ trade, slug: cityMoneySlug(trade.slug, city, st) }));
